export const CONTRACT_TYPES = ["Preneed Funeral", "Cemetery Property"];

export const STATUS_COLORS = {
  Accepted: "#10b981", // green
  Pending: "#f59e0b", // amber
  Delivered: "#3b82f6", // blue
  Cancelled: "#ef4444", // red
  Abandoned: "#9ca3af", // gray
};
export const TREND_RANGE_OPTIONS = [
  { value: "3m", label: "Last 3 months" },
  { value: "6m", label: "Last 6 months" },
  { value: "12m", label: "Last 12 months" },
  { value: "ytd", label: "Year to date" },
];

export const NOTATION_CATEGORY_THEMES = [
  {
    bg: "#e6f1fb",
    accent: "#378ADD",
    titleColor: "#0c447c",
    ramp: [
      "#0c447c",
      "#185fa5",
      "#378add",
      "#5ba0e5",
      "#85b7eb",
      "#b5d4f4",
      "#d8e8f8",
    ],
  },
  {
    bg: "#e6fbf1",
    accent: "#37dd8a",
    titleColor: "#0c7c44",
    ramp: [
      "#0c7c44",
      "#18a55e",
      "#37dd8a",
      "#5be5a0",
      "#85ebb8",
      "#b5f4d4",
      "#d8f8e8",
    ],
  },
];

export const CreditCardPaymentDescription =
  "Easy access to contract credit card payment processing.";

export function getRangeDates(range) {
  // Custom — use as-is
  if (typeof range === "object" && range?.value === "custom") {
    return {
      startDate: range.startDate,
      endDate: range.endDate,
    };
  }

  // Preset — compute from today
  const end = new Date();
  const start = new Date();
  switch (range) {
    case "3m":
      start.setMonth(start.getMonth() - 3);
      break;
    case "6m":
      start.setMonth(start.getMonth() - 6);
      break;
    case "12m":
      start.setMonth(start.getMonth() - 12);
      break;
    case "ytd":
      start.setMonth(0);
      start.setDate(1);
      break;
    default:
      start.setMonth(start.getMonth() - 6);
  }

  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

export const NOTATION_CATEGORY_LABELS = {
  Disbursement: "Pending Disbursement",
  Pending: "Pending New Business",
};

export function getCategoryLabel(rawCategory) {
  return NOTATION_CATEGORY_LABELS[rawCategory] || rawCategory;
}

// ============================================================
//  Currency
// ============================================================

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const usdCompact = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

/**
 * Format a number as USD currency with 2 decimals.
 * Equivalent to C# "C2" format.
 *
 * formatCurrency(1234.56)   → "$1,234.56"
 * formatCurrency(0)         → "$0.00"
 * formatCurrency(null)      → "$0.00"
 * formatCurrency(undefined) → "$0.00"
 */
export function formatCurrency(value) {
  if (value == null || isNaN(value)) return "$0.00";
  return usd.format(Number(value));
}

/**
 * Compact currency for tight spaces like chart axes.
 *
 * formatCurrencyCompact(1234)      → "$1.2K"
 * formatCurrencyCompact(1234567)   → "$1.2M"
 * formatCurrencyCompact(0)         → "$0"
 */
export function formatCurrencyCompact(value) {
  if (value == null || isNaN(value)) return "$0";
  return usdCompact.format(Number(value));
}

// ============================================================
//  Numbers (counts, quantities)
// ============================================================

const number = new Intl.NumberFormat("en-US");

const numberCompact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/**
 * Format a number with thousands separators.
 *
 * formatNumber(1234)    → "1,234"
 * formatNumber(508100)  → "508,100"
 * formatNumber(null)    → "0"
 */
export function formatNumber(value) {
  if (value == null || isNaN(value)) return "0";
  return number.format(Number(value));
}

/**
 * Compact number for tight spaces.
 *
 * formatNumberCompact(1234)     → "1.2K"
 * formatNumberCompact(1234567)  → "1.2M"
 * formatNumberCompact(0)        → "0"
 */
export function formatNumberCompact(value) {
  if (value == null || isNaN(value)) return "0";
  return numberCompact.format(Number(value));
}

// ============================================================
//  Percent
// ============================================================

const percent = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/**
 * Format a decimal (0-1) as a percent.
 *
 * formatPercent(0.125)  → "13%"
 * formatPercent(0.1)    → "10%"
 * formatPercent(1)      → "100%"
 */
export function formatPercent(value) {
  if (value == null || isNaN(value)) return "0%";
  return percent.format(Number(value));
}
